'use client';

import type { BoxProps } from '@mui/material/Box';
import type { Theme, SxProps } from '@mui/material/styles';

import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { Iconify } from 'src/components/iconify';
import { Scrollbar as ScrollbarBase } from 'src/components/scrollbar';
import { AnimateBorder as AnimateBorderBase } from 'src/components/animate';

import { useAuthContext } from 'src/auth/hooks';

import { UpgradeBlock } from './nav-upgrade';
import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';
import { AccountMenuItems } from './account-menu-items';

// ----------------------------------------------------------------------

// Scrollbar·AnimateBorder 는 아직 JS 라 props 가 전부 필수로 추론된다 — 실제 계약(전부 선택)으로 좁혀 쓴다 (로직 무변)
const Scrollbar = ScrollbarBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  fillContent?: boolean;
  children?: React.ReactNode;
  className?: string;
}>;

const AnimateBorder = AnimateBorderBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  duration?: number;
  slotProps?: Record<string, unknown>;
  className?: string;
  children?: React.ReactNode;
}>;

type AccountDrawerProps = BoxProps;

export function AccountDrawer({ sx, ...other }: AccountDrawerProps) {

  // const { user } = useMockedUser();
  const { user } = useAuthContext();

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const renderAvatar = () => (
    <AnimateBorder
      sx={{ p: '4px', width: 64, height: 64, borderRadius: '50%' }}
      slotProps={{
        primaryBorder: { size: 80, sx: { color: 'primary.main' } },
      }}
    >
      <Avatar
        src={user?.photoURL as string | undefined}
        alt={user?.displayName as string | undefined}
        sx={{ width: 1, height: 1 }}
      >
        { user?.display_name || (user?.displayName as string | undefined)?.charAt(0).toUpperCase()}
      </Avatar>
    </AnimateBorder>
  );

  /* 🔑 항목은 사이드바 발치 메뉴와 한 벌이다(`AccountMenuItems` · `nav-config-account`) */
  const renderList = () => (
    <MenuList
      disablePadding
      sx={{ px: 2, mt: 2 }}
    >
      <AccountMenuItems onDone={onClose} divided={false} help={false} />
    </MenuList>
  );

  return (
    <>
      <AccountButton
        onClick={onOpen}
        photoURL={user?.photoURL as string | undefined}
        displayName={user?.display_name || (user?.displayName as string | undefined)}
        email={user?.email}
        sx={sx}
        {...other}
      />

      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
          paper: { 
            sx: { 
              width: { xs: 200, sm: 300, md: 350 },
            } 
          },
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            top: 12,
            left: 12,
            zIndex: 9,
            position: 'absolute',
          }}
        >
          <Iconify icon="solar:close-circle-linear" />
        </IconButton>

        <Scrollbar>
          <Box
            sx={{
              pt: 6,
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            {renderAvatar()}

            <Typography variant="subtitle1" noWrap sx={{ mt: 1.5 }}>
              {user?.display_name || (user?.displayName as string | undefined)}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
              {user?.email}
            </Typography>
          </Box>
{/* 
          <Box
            sx={{
              p: 3,
              gap: 1,
              flexWrap: 'wrap',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {Array.from({ length: 3 }, (_, index) => (
              <Tooltip
                key={_mock.fullName(index + 1)}
                title={`Switch to: ${_mock.fullName(index + 1)}`}
              >
                <Avatar
                  alt={_mock.fullName(index + 1)}
                  src={_mock.image.avatar(index + 1)}
                  onClick={() => {}}
                />
              </Tooltip>
            ))}

            <Tooltip title="Add account">
              <IconButton
                sx={[
                  (theme) => ({
                    bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
                    border: `dashed 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.32)}`,
                  }),
                ]}
              >
                <Iconify icon="solar:add-linear" />
              </IconButton>
            </Tooltip>
          </Box> */}

          {renderList()}

          <Box sx={{ px: 2.5, py: 2 }}>
            <UpgradeBlock />
          </Box>
        </Scrollbar>

        <Box sx={{ p: 2.5 }}>
          <SignOutButton onClose={onClose} />
        </Box>
      </Drawer>

    </>
  );
}
